import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/about.css';

const About = () => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [activeGalleryImage, setActiveGalleryImage] = useState(0);
  const navigate = useNavigate();
  const statsRef = useRef(null);
  const observerRef = useRef(null);

  // Company stats data
  const companyStats = [
    { number: '10,000+', label: 'Happy Customers', icon: 'fas fa-users' },
    { number: '50,000+', label: 'Products Listed', icon: 'fas fa-box' },
    { number: '5,000+', label: 'Successful Jobs', icon: 'fas fa-briefcase' },
    { number: '95%', label: 'Satisfaction Rate', icon: 'fas fa-star' },
  ];

  // Team members data
  const teamMembers = [
    {
      name: 'John Smith',
      position: 'CEO & Founder',
      image:
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      social: { linkedin: '#', twitter: '#', email: '#' },
    },
    {
      name: 'Sarah Johnson',
      position: 'CTO',
      image:
        'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400',
      social: { linkedin: '#', github: '#', email: '#' },
    },
    {
      name: 'Mike Chen',
      position: 'Head of Marketing',
      image:
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      social: { linkedin: '#', twitter: '#', instagram: '#' },
    },
    {
      name: 'Emily Davis',
      position: 'Product Manager',
      image:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
      social: { linkedin: '#', twitter: '#', email: '#' },
    },
  ];

  // Milestones data
  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description:
        'Started with a vision to revolutionize advertising in Sri Lanka',
    },
    {
      year: '2021',
      title: 'Platform Launch',
      description:
        'Launched our first version with product advertising features',
    },
    {
      year: '2022',
      title: 'Job Portal Added',
      description: 'Expanded to include comprehensive job portal services',
    },
    {
      year: '2023',
      title: '10K Customers',
      description: 'Reached milestone of 10,000 satisfied customers',
    },
    {
      year: '2024',
      title: 'National Expansion',
      description: 'Expanded services to cover all major cities in Sri Lanka',
    },
  ];

  // Values data
  const values = [
    {
      icon: 'fas fa-handshake',
      title: 'Trust & Transparency',
      description:
        'We believe in building relationships based on honesty and clear communication.',
    },
    {
      icon: 'fas fa-rocket',
      title: 'Innovation',
      description:
        'Constantly evolving to bring you the latest and most effective advertising solutions.',
    },
    {
      icon: 'fas fa-users-cog',
      title: 'Customer First',
      description:
        'Your success is our success. We prioritize your needs in everything we do.',
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Quality Assurance',
      description:
        'Maintaining the highest standards in all our services and partnerships.',
    },
  ];

  // Gallery images
  const galleryImages = [
    'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    'https://images.unsplash.com/photo-1551836026-d5c2e0c49b13?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
  ];

  useEffect(() => {
    // Initialize animations and counters
    setupAnimations();
    startCounterAnimation();

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const setupAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe elements for animation
    document
      .querySelectorAll('.timeline-item, .value-card, .team-card, .mv-card')
      .forEach((el) => {
        if (observerRef.current) {
          observerRef.current.observe(el);
        }
      });
  };

  const startCounterAnimation = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounters();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
  };

  const animateCounters = () => {
    const statItems = document.querySelectorAll('.stat-item');

    statItems.forEach((statItem) => {
      const numberElement = statItem.querySelector('.stat-number');
      const targetNumber = statItem.dataset.count;

      if (!numberElement || !targetNumber) return;

      // Remove commas and parse number
      const target = parseInt(targetNumber.replace(/,/g, ''));
      const duration = 2000;
      const step = target / (duration / 16);
      let current = 0;

      const timer = setInterval(() => {
        current += step;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }

        // Format number with commas
        numberElement.textContent = Math.floor(current).toLocaleString();
        numberElement.classList.add('counting');
      }, 16);

      // Remove counting class after animation
      setTimeout(() => {
        numberElement.classList.remove('counting');
      }, duration + 100);
    });
  };

  const openVideoModal = () => {
    setVideoModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    document.body.style.overflow = '';
  };

  const handleGalleryThumbnailClick = (index) => {
    setActiveGalleryImage(index);
  };

  const handleKeyPress = (e, callback, ...args) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback(...args);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Effect to handle escape key for modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && videoModalOpen) {
        closeVideoModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [videoModalOpen]);

  return (
    <>
      <main>
        {/* Hero Section */}
        <section className='about-hero' aria-labelledby='hero-title'>
          <div className='hero-background'>
            <div className='hero-overlay'></div>
          </div>
          <div className='container'>
            <div className='hero-content'>
              <h1 id='hero-title'>Transforming Advertising in Sri Lanka</h1>
              <p className='hero-subtitle'>
                Connecting businesses with opportunities through innovative
                advertising and job portal solutions
              </p>
              <div className='hero-actions'>
                <button
                  className='btn btn-primary'
                  onClick={openVideoModal}
                  aria-label='Watch our company story video'
                >
                  <i className='fas fa-play-circle'></i> Watch Our Story
                </button>
                <a
                  href='#contact'
                  className='btn btn-secondary'
                  onClick={(e) => {
                    e.preventDefault();
                    // Scroll to contact section or navigate
                  }}
                >
                  <i className='fas fa-envelope'></i> Get In Touch
                </a>
              </div>
            </div>
            <div className='hero-stats' ref={statsRef}>
              {companyStats.map((stat, index) => (
                <div key={index} className='stat-item' data-count={stat.number}>
                  <i className={stat.icon} aria-hidden='true'></i>
                  <div className='stat-content'>
                    <span className='stat-number'>0</span>
                    <span className='stat-label'>{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className='story-section' aria-labelledby='story-title'>
          <div className='container'>
            <div className='section-header'>
              <h2 id='story-title'>Our Story</h2>
              <p className='section-subtitle'>
                From humble beginnings to becoming Sri Lanka's premier
                advertising platform
              </p>
            </div>
            <div className='story-content'>
              <div className='story-text'>
                <div className='story-highlight'>
                  <h3>Building Bridges Between Businesses and Opportunities</h3>
                  <p>
                    Founded in 2020, Fahple emerged from a simple observation:
                    businesses in Sri Lanka needed a more effective way to reach
                    their target audience, while job seekers struggled to find
                    meaningful opportunities.
                  </p>
                </div>
                <div className='story-details'>
                  <p>
                    We started as a small team with a big vision - to create a
                    platform that would revolutionize how advertising and
                    recruitment work in Sri Lanka. Today, we're proud to serve
                    thousands of businesses and individuals across the country.
                  </p>
                  <p>
                    Our journey has been guided by our core belief that
                    technology should make life easier, not more complicated.
                    That's why we've built Fahple to be intuitive, powerful, and
                    accessible to everyone.
                  </p>
                </div>
                <div className='mission-vision'>
                  <div className='mv-card'>
                    <i className='fas fa-bullseye' aria-hidden='true'></i>
                    <h4>Our Mission</h4>
                    <p>
                      To empower businesses and individuals by providing
                      innovative advertising and job portal solutions that drive
                      growth and create opportunities.
                    </p>
                  </div>
                  <div className='mv-card'>
                    <i className='fas fa-eye' aria-hidden='true'></i>
                    <h4>Our Vision</h4>
                    <p>
                      To become Sri Lanka's most trusted platform for connecting
                      businesses with customers and talent with opportunities.
                    </p>
                  </div>
                </div>
              </div>
              <div className='story-visual'>
                <div className='image-gallery'>
                  <img
                    src={galleryImages[activeGalleryImage]}
                    alt='Fahple team collaboration'
                    loading='lazy'
                    className='gallery-main'
                  />
                  <div className='gallery-thumbnails'>
                    {galleryImages.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Gallery thumbnail ${index + 1}`}
                        loading='lazy'
                        className={index === activeGalleryImage ? 'active' : ''}
                        onClick={() => handleGalleryThumbnailClick(index)}
                        onKeyDown={(e) =>
                          handleKeyPress(e, handleGalleryThumbnailClick, index)
                        }
                        tabIndex='0'
                        role='button'
                        aria-label={`View image ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className='timeline-section' aria-labelledby='timeline-title'>
          <div className='container'>
            <div className='section-header'>
              <h2 id='timeline-title'>Our Journey</h2>
              <p className='section-subtitle'>
                Key milestones in our growth story
              </p>
            </div>
            <div className='timeline'>
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className='timeline-item'
                  data-year={milestone.year}
                >
                  <div className='timeline-marker'>
                    <span className='year'>{milestone.year}</span>
                    <div className='dot'></div>
                  </div>
                  <div className='timeline-content'>
                    <h3>{milestone.title}</h3>
                    <p>{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className='values-section' aria-labelledby='values-title'>
          <div className='container'>
            <div className='section-header'>
              <h2 id='values-title'>Our Values</h2>
              <p className='section-subtitle'>
                The principles that guide everything we do
              </p>
            </div>
            <div className='values-grid'>
              {values.map((value, index) => (
                <div key={index} className='value-card'>
                  <div className='value-icon'>
                    <i className={value.icon} aria-hidden='true'></i>
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className='team-section' aria-labelledby='team-title'>
          <div className='container'>
            <div className='section-header'>
              <h2 id='team-title'>Meet Our Team</h2>
              <p className='section-subtitle'>
                The passionate people behind Fahple's success
              </p>
            </div>
            <div className='team-grid'>
              {teamMembers.map((member, index) => (
                <div key={index} className='team-card'>
                  <div className='team-image'>
                    <img src={member.image} alt={member.name} loading='lazy' />
                    <div className='team-overlay'>
                      <div className='social-links'>
                        {Object.entries(member.social).map(
                          ([platform, url]) => (
                            <a
                              key={platform}
                              href={url}
                              aria-label={`Connect with ${member.name} on ${
                                platform.charAt(0).toUpperCase() +
                                platform.slice(1)
                              }`}
                              target='_blank'
                              rel='noopener noreferrer'
                            >
                              <i className={`fab fa-${platform}`}></i>
                            </a>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                  <div className='team-info'>
                    <h3>{member.name}</h3>
                    <p className='position'>{member.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className='cta-section' aria-labelledby='cta-title'>
          <div className='container'>
            <div className='cta-content'>
              <h2 id='cta-title'>Ready to Grow With Us?</h2>
              <p>
                Join thousands of businesses already using Fahple to reach their
                goals
              </p>
              <div className='cta-buttons'>
                <button
                  className='btn btn-primary btn-large'
                  onClick={() => handleNavigation('/products')}
                >
                  <i className='fas fa-shopping-cart'></i> Start Advertising
                </button>
                <button
                  className='btn btn-secondary btn-large'
                  onClick={() => handleNavigation('/job')}
                >
                  <i className='fas fa-briefcase'></i> Post a Job
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Video Modal */}
      {videoModalOpen && (
        <div
          className='video-modal active'
          id='videoModal'
          role='dialog'
          aria-labelledby='videoModalTitle'
        >
          <div className='modal-backdrop' onClick={closeVideoModal}></div>
          <div className='modal-content'>
            <div className='modal-header'>
              <h3 id='videoModalTitle'>Our Story</h3>
              <button
                className='modal-close'
                onClick={closeVideoModal}
                aria-label='Close video'
              >
                <i className='fas fa-times'></i>
              </button>
            </div>
            <div className='modal-body'>
              <div className='video-placeholder'>
                <i className='fas fa-play-circle' aria-hidden='true'></i>
                <p>Company story video would play here</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default About;
